// Public landing-split page (Mitchell 2026-04-25 — dev2 brief).
//
// Rendered before auth for unauthenticated visitors at `/`, `/signup`,
// and `/guest`. Discord-style two-column card with a Sign Up form on
// the left and a Continue as Guest column on the right, plus a small
// "I already have a PIN" link at the bottom.
//
// `/signup` and `/guest` are the same module — we just open the
// page on the matching panel based on path. This keeps a single shell,
// a single style block, and lets the user flip between modes without
// a navigation reload.

import { API_BASE } from '../api';

interface ApiResponse<T> { status: 'ok' | 'error'; data?: T; error?: string; }

interface SignupResp {
  person_id: string;
  redirect_url: string;
}

interface GuestStartResp {
  guest_id: string;
  pin: string;
  expires_at: string;
}

interface GuestResumeResp {
  guest_id: string;
  expires_at: string;
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });
  const json = (await res.json().catch(() => ({ status: 'error', error: 'bad json' }))) as ApiResponse<T>;
  if (json.status === 'error' || !res.ok) {
    throw new Error(json.error ?? `HTTP ${res.status}`);
  }
  return json.data as T;
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function injectStyles(): void {
  if (document.getElementById('hp-landing-styles')) return;
  const style = document.createElement('style');
  style.id = 'hp-landing-styles';
  style.textContent = `
    .hp-landing-page { width: 100vw; min-height: 100dvh; background: var(--surface-0); color: var(--text-primary); display: flex; align-items: center; justify-content: center; padding: 32px 16px; font-family: system-ui, -apple-system, Segoe UI, sans-serif; box-sizing: border-box; }
    .hp-landing-shell { width: 100%; max-width: 880px; margin: 0 auto; text-align: center; display: flex; flex-direction: column; align-items: center; }
    .hp-landing-head { text-align: center; margin-bottom: 24px; display: flex; flex-direction: column; align-items: center; }
    .hp-landing-logo { display: inline-flex; align-items: center; justify-content: center; width: 56px; height: 56px; margin-bottom: 16px; }
    .hp-landing-logo img { width: 100%; height: 100%; object-fit: contain; }
    .hp-landing-title { color: var(--text-primary); font-size: 36px; line-height: 1.08; font-weight: 800; margin: 0 0 8px; }
    .hp-landing-sub { color: var(--text-secondary); font-size: 15px; line-height: 1.4; margin: 0; }
    .hp-landing-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
    @media (max-width: 720px) { .hp-landing-grid { grid-template-columns: 1fr; } }
    .hp-landing-card { width: 100%; max-width: 500px; background: var(--surface-1); border: 1px solid var(--border-subtle); border-radius: var(--radius-lg); padding: 32px; display: flex; flex-direction: column; align-items: center; gap: 16px; box-sizing: border-box; box-shadow: 0 1px 0 var(--border-subtle), 0 24px 80px var(--surface-0); }
    .hp-landing-card h2 { color: var(--text-primary); font-size: 24px; line-height: 1.18; font-weight: 700; margin: 0; }
    .hp-landing-card .hp-landing-tag { font-size: 12px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.06em; margin: 0; }
    .hp-landing-card p.hp-landing-blurb { max-width: 390px; color: var(--text-secondary); font-size: 15px; line-height: 1.6; margin: 0; }
    .hp-landing-card p.hp-landing-note { color: var(--text-muted); font-size: 13px; line-height: 1.55; }
    .hp-landing-field { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: var(--text-secondary); }
    .hp-landing-field input { background: var(--surface-2); border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); color: var(--text-primary); font-size: 15px; padding: 10px 12px; outline: none; }
    .hp-landing-field input:focus { border-color: var(--accent); }
    .hp-landing-btn { display: block; width: 100%; background: var(--accent); color: var(--surface-0); border: none; border-radius: var(--radius-sm); font-size: 15px; font-weight: 600; padding: 12px; cursor: pointer; }
    .hp-landing-btn:hover:not(:disabled) { filter: brightness(1.1); }
    .hp-landing-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .hp-landing-btn-ghost { background: transparent; color: var(--text-primary); border: 1px solid var(--border-subtle); }
    .hp-landing-error { color: var(--text-muted); font-size: 13px; min-height: 1em; }
    .hp-landing-foot { text-align: center; font-size: 13px; margin-top: 22px; color: var(--text-secondary); }
    .hp-landing-foot a { color: var(--accent); text-decoration: none; }
    .hp-landing-foot a:hover { text-decoration: underline; }
    .hp-pin-show { background: var(--surface-2); border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); font-family: ui-monospace, SFMono-Regular, Menospace, monospace; font-size: 32px; font-weight: 700; letter-spacing: 0.5em; text-align: center; padding: 14px 0; color: var(--text-primary); }
    .hp-pin-warn { color: var(--text-secondary); font-size: 13px; line-height: 1.5; margin: 0; }
    .hp-pin-row { display: flex; gap: 10px; }
    .hp-pin-row .hp-landing-btn { flex: 1; }
    .hp-landing-input-pin { letter-spacing: 0.4em; text-align: center; font-size: 22px; font-weight: 600; }
    @media (min-width: 481px) and (max-width: 768px) {
      .hp-landing-page { padding: 40px 24px; }
      .hp-landing-card { max-width: 520px; padding: 34px; }
    }
    @media (min-width: 769px) {
      .hp-landing-page { padding: 56px 24px; }
      .hp-landing-head { margin-bottom: 28px; }
      .hp-landing-card { padding: 38px; }
    }
    @media (max-width: 480px) {
      .hp-landing-page { padding: 24px 16px; }
      .hp-landing-logo { width: 48px; height: 48px; margin-bottom: 14px; }
      .hp-landing-title { font-size: 30px; }
      .hp-landing-sub { font-size: 14px; }
      .hp-landing-head { margin-bottom: 22px; }
      .hp-landing-card { max-width: none; padding: 24px 20px; gap: 14px; }
      .hp-landing-card h2 { font-size: 22px; }
      .hp-landing-card p.hp-landing-blurb { font-size: 14px; line-height: 1.58; }
    }
  `;
  document.head.appendChild(style);
}

function shell(title: string): HTMLElement {
  document.title = title;
  injectStyles();
  const root = (document.getElementById('app') ?? document.body) as HTMLElement;
  root.innerHTML = '';
  const page = document.createElement('div');
  page.className = 'hp-landing-page';
  root.appendChild(page);
  return page;
}

function downloadPin(pin: string, expiresAt: string): void {
  const lines = [
    'Hub People — Guest PIN',
    '',
    `PIN: ${pin}`,
    `Expires: ${expiresAt}`,
    '',
    "Keep this safe. You'll need it to come back to your guest session.",
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'hub-people-guest-pin.txt';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function _renderSignup(panel: HTMLElement): void {
  panel.innerHTML = `
    <p class="hp-landing-tag">Full account</p>
    <h2>Sign Up</h2>
    <p class="hp-landing-blurb">Create your hub. Servers, direct messages,
    bookings — everything. Your data stays on your hub.</p>
    <label class="hp-landing-field">
      <span>Display name</span>
      <input id="hp-signup-name" type="text" maxlength="80" autocomplete="name" />
    </label>
    <label class="hp-landing-field">
      <span>Email</span>
      <input id="hp-signup-email" type="email" maxlength="200" autocomplete="email" />
    </label>
    <button id="hp-signup-go" class="hp-landing-btn" type="button">Create account</button>
    <div id="hp-signup-error" class="hp-landing-error"></div>
  `;
  const nameEl = panel.querySelector<HTMLInputElement>('#hp-signup-name');
  const emailEl = panel.querySelector<HTMLInputElement>('#hp-signup-email');
  const btn = panel.querySelector<HTMLButtonElement>('#hp-signup-go');
  const err = panel.querySelector<HTMLElement>('#hp-signup-error');
  if (!nameEl || !emailEl || !btn || !err) return;

  const submit = async (): Promise<void> => {
    err.textContent = '';
    const name = nameEl.value.trim();
    const email = emailEl.value.trim();
    if (!name) { err.textContent = 'Name required'; return; }
    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = 'Creating...';
    try {
      const data = await apiPost<SignupResp>('/api/signup', {
        name,
        email: email || null,
      });
      window.location.href = data.redirect_url || '/';
    } catch (e) {
      err.textContent = String((e as Error).message || e);
      btn.disabled = false;
      btn.textContent = original;
    }
  };
  btn.addEventListener('click', submit);
  emailEl.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') void submit();
  });
}

function _renderGuestStart(panel: HTMLElement): void {
  panel.innerHTML = `
    <p class="hp-landing-tag">Try it</p>
    <h2>Continue as Guest</h2>
    <p class="hp-landing-blurb">Look around the guest playground server.
    No account, no email — just a 4-digit PIN to come back later.</p>
    <button id="hp-guest-go" class="hp-landing-btn" type="button">Get my PIN</button>
    <div id="hp-guest-error" class="hp-landing-error"></div>
    <div class="hp-landing-foot">
      <a id="hp-guest-resume-link" href="/guest">I already have a PIN</a>
    </div>
  `;
  const btn = panel.querySelector<HTMLButtonElement>('#hp-guest-go');
  const err = panel.querySelector<HTMLElement>('#hp-guest-error');
  if (!btn || !err) return;
  btn.addEventListener('click', async () => {
    err.textContent = '';
    btn.disabled = true;
    btn.textContent = 'Generating...';
    try {
      const data = await apiPost<GuestStartResp>('/api/guest/start', {});
      renderPinReveal(panel, data);
    } catch (e) {
      err.textContent = String((e as Error).message || e);
      btn.disabled = false;
      btn.textContent = 'Get my PIN';
    }
  });
}

function renderPinReveal(panel: HTMLElement, data: GuestStartResp): void {
  panel.innerHTML = `
    <p class="hp-landing-tag">Save this PIN</p>
    <h2>Your PIN</h2>
    <div class="hp-pin-show">${esc(data.pin)}</div>
    <p class="hp-pin-warn">Save it — you'll need it to come back.
    Expires ${esc(data.expires_at.slice(0, 10))}.</p>
    <div class="hp-pin-row">
      <button id="hp-pin-copy" class="hp-landing-btn hp-landing-btn-ghost" type="button">Copy</button>
      <button id="hp-pin-download" class="hp-landing-btn hp-landing-btn-ghost" type="button">Download</button>
    </div>
    <button id="hp-pin-enter" class="hp-landing-btn" type="button">Enter Hub</button>
    <div id="hp-pin-toast" class="hp-landing-error"></div>
  `;
  const copyBtn = panel.querySelector<HTMLButtonElement>('#hp-pin-copy');
  const dlBtn = panel.querySelector<HTMLButtonElement>('#hp-pin-download');
  const enterBtn = panel.querySelector<HTMLButtonElement>('#hp-pin-enter');
  const toast = panel.querySelector<HTMLElement>('#hp-pin-toast');
  copyBtn?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(data.pin);
      if (toast) toast.textContent = 'PIN copied to clipboard';
    } catch {
      if (toast) toast.textContent = 'Copy unavailable — use Download';
    }
  });
  dlBtn?.addEventListener('click', () => downloadPin(data.pin, data.expires_at));
  enterBtn?.addEventListener('click', () => {
    window.location.href = '/';
  });
}

function _renderSignIn(panel: HTMLElement): void {
  panel.innerHTML = `
    <p class="hp-landing-tag">Welcome back</p>
    <h2>Sign In</h2>
    <p class="hp-landing-blurb">Enter your PIN to pick up where you left off.</p>
    <label class="hp-landing-field">
      <span>PIN</span>
      <input id="hp-login-pin" class="hp-landing-input-pin" type="password" inputmode="numeric"
             maxlength="8" autocomplete="current-password" />
    </label>
    <button id="hp-login-go" class="hp-landing-btn" type="button">Sign In</button>
    <div id="hp-login-error" class="hp-landing-error"></div>
    <div class="hp-landing-foot" style="text-align:left;margin-top:4px">
      <a href="/login">Advanced login (seed phrase)</a>
    </div>
  `;
  const pinEl = panel.querySelector<HTMLInputElement>('#hp-login-pin');
  const btn = panel.querySelector<HTMLButtonElement>('#hp-login-go');
  const err = panel.querySelector<HTMLElement>('#hp-login-error');
  if (!pinEl || !btn || !err) return;
  pinEl.focus();

  const submit = async (): Promise<void> => {
    err.textContent = '';
    const pin = pinEl.value.trim();
    if (!pin || pin.length < 4) { err.textContent = 'Enter your PIN'; return; }
    btn.disabled = true;
    btn.textContent = 'Signing in...';
    try {
      // Try dev-pin first (hub-core direct), then pin-login (hub-people)
      let ok = false;
      try {
        const devRes = await fetch(`${API_BASE}/api/auth/dev-pin`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pin }),
        });
        if (devRes.ok) ok = true;
      } catch { /* try normal */ }
      if (!ok) {
        const res = await fetch(`${API_BASE}/api/auth/pin-login`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pin }),
        });
        if (res.ok) ok = true;
        else if (res.status === 405 || res.status === 404) {
          window.location.href = '/login';
          return;
        }
      }
      if (ok) { window.location.reload(); return; }
      err.textContent = 'Incorrect PIN';
    } catch {
      err.textContent = 'Network error';
    }
    btn.disabled = false;
    btn.textContent = 'Sign In';
  };
  btn.addEventListener('click', submit);
  pinEl.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') void submit();
  });
}

function _renderGuestResume(panel: HTMLElement): void {
  panel.innerHTML = `
    <p class="hp-landing-tag">Returning guest</p>
    <h2>Resume with PIN</h2>
    <p class="hp-landing-blurb">Enter the 4-digit PIN you saved last time
    to pick up where you left off.</p>
    <label class="hp-landing-field">
      <span>PIN</span>
      <input id="hp-guest-pin" class="hp-landing-input-pin" type="tel" inputmode="numeric"
             maxlength="4" pattern="[0-9]{4}" autocomplete="off" />
    </label>
    <button id="hp-guest-resume" class="hp-landing-btn" type="button">Resume</button>
    <div id="hp-guest-resume-error" class="hp-landing-error"></div>
    <div class="hp-landing-foot">
      <a id="hp-guest-back" href="/">Back to landing</a>
    </div>
  `;
  const pinEl = panel.querySelector<HTMLInputElement>('#hp-guest-pin');
  const btn = panel.querySelector<HTMLButtonElement>('#hp-guest-resume');
  const err = panel.querySelector<HTMLElement>('#hp-guest-resume-error');
  if (!pinEl || !btn || !err) return;
  pinEl.focus();
  const submit = async (): Promise<void> => {
    err.textContent = '';
    const pin = pinEl.value.trim();
    if (!/^\d{4}$/.test(pin)) {
      err.textContent = 'PIN must be 4 digits';
      return;
    }
    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = 'Checking...';
    try {
      await apiPost<GuestResumeResp>('/api/guest/resume', { pin });
      window.location.href = '/';
    } catch (e) {
      err.textContent = String((e as Error).message || e);
      btn.disabled = false;
      btn.textContent = original;
    }
  };
  btn.addEventListener('click', submit);
  pinEl.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') void submit();
  });
}

function _renderHead(page: HTMLElement, mode: LandingMode): void {
  const head = document.createElement('div');
  head.className = 'hp-landing-head';
  const titles: Record<LandingMode, string> = {
    landing: 'Hub People',
    signup: 'Create your account',
    guest: 'Resume guest session',
    login: 'Hub People',
  };
  const subs: Record<LandingMode, string> = {
    landing: 'Two ways in. Both yours.',
    signup: 'Your hub. Your data. Your control.',
    guest: 'Punch in your PIN to resume.',
    login: 'Sign in to continue.',
  };
  head.innerHTML = `
    <div class="hp-landing-logo"><img src="/logo.svg" alt="Hub People" /></div>
    <h1 class="hp-landing-title">${esc(titles[mode])}</h1>
    <p class="hp-landing-sub">${esc(subs[mode])}</p>
  `;
  page.appendChild(head);
}

type LandingMode = 'landing' | 'signup' | 'guest' | 'login';

function modeFromPath(): LandingMode {
  const path = window.location.pathname;
  if (path.endsWith('/signup') || path.endsWith('/people/signup')) return 'signup';
  if (path.endsWith('/guest') || path.endsWith('/people/guest')) return 'guest';
  // Any deep-link path (e.g. /channels/...) that reaches us unauthed should
  // present the sign-in form so the user can get back to their intended page.
  if (path === '/' || path === '/people' || path === '/people/') return 'landing';
  return 'login';
}

function renderLandingShell(page: HTMLElement, _mode: LandingMode): void {
  const shellEl = document.createElement('div');
  shellEl.className = 'hp-landing-shell';
  page.appendChild(shellEl);

  shellEl.innerHTML = `
    <div class="hp-landing-head">
      <div class="hp-landing-logo"><img src="./favicon.svg" alt=""></div>
      <h1 class="hp-landing-title">Hub People</h1>
      <p class="hp-landing-sub">by the hub ecosystems</p>
    </div>
    <div class="hp-landing-card">
      <h2>Coming Soon</h2>
      <p class="hp-landing-blurb">Hub People is not yet open for sign-ups. We are building something worth waiting for.</p>
      <p class="hp-landing-blurb hp-landing-note">If you have been invited as a guest, use the link provided to you.</p>
    </div>
  `;
}

export async function renderLanding(): Promise<void> {
  const mode = modeFromPath();
  const titleMap: Record<LandingMode, string> = {
    landing: 'Hub People',
    signup: 'Create account — Hub People',
    guest: 'Resume guest — Hub People',
    login: 'Sign in — Hub People',
  };
  const page = shell(titleMap[mode]);
  renderLandingShell(page, mode);
}

/** Returns true when the path is a public landing-split entry point.
 *  Now that the landing page handles ALL unauth surfaces (including deep
 *  links via the 'login' mode), this is mainly used for pre-auth routing
 *  decisions by other modules. */
export function isPublicLandingPath(): boolean {
  const path = window.location.pathname;
  // root or proxy root
  if (path === '/' || path === '/people' || path === '/people/') return true;
  if (/^\/(?:people\/)?signup\/?$/.test(path)) return true;
  if (/^\/(?:people\/)?guest\/?$/.test(path)) return true;
  return false;
}
